import React from 'react'
import { observer } from 'mobx-react'
import { SideSheetHostView, useSideSheet } from 'components/SideSheet'
import JobViewModel from './JobViewModel'
import HeaderView from './HeaderView'
import ContentView from './ContentView'
import CreateAmendmentDialog from 'jobs/views/JobRoot/Job/AmendmentsCard/CreateAmendmentDialog'
import DocumentPicker from 'documents/views/DocumentPicker'
import { useDropzone } from 'hooks/use-dropzone'
import CorrectionsDialog from './CorrectionsDialog'
import RenameJobDialog from 'jobs/views/RenameJobDialog'
import AddJobNoteDialog from './JobNotesCard/AddJobNoteDialog'
import ForfeitDialog from './ForfeitDialog'
import MessageBankDialog from 'messaging/message-bank/MessageBankDialog'
import CloseJobAsAdminDialog from 'jobs/views/CloseJobDialog/CloseJobAsAdminDialog'
import RequestScopeChangesDialog from './RequestScopeChangesDialog'
import CopyJobDialog from 'jobs/views/CopyJobDialog'
import TransferJobDialog from './TransferJobDialog'
import RemoveCollaboratorsAsAdminDialog from './RemoveCollaboratorsAsAdminDialog'
import AddCollaboratorsAsAdminDialog from './AddCollaboratorsAsAdminDialog'
import ApplyCouponAsAdminDialog from './ApplyCouponAsAdminDialog'
import RemoveCouponAsAdminDialog from './RemoveCouponAsAdminDialog'
import styles from './JobCards.sass'
import ReopenJobAsAdminDialog from './ReopenJobAsAdminDialog'
import RemoveClientTeamDialog from './RemoveClientTeamDialog'
import ViewJobScopeChangeRequestDialog from './ViewJobScopeChangeRequestDialog'
import SplitPayoutDialog from './SplitPayoutDialog'
import DocumentCreationModal from 'documents/views/DocumentCreationModal'
import ApproveDraftDialog from './ApproveDraftInReviewDialog'
import RejectDraftDialog from './RejectDraftInReviewDialog'
import DeleteJobDialog from './DeleteJobDialog'
import StartProgressionDialog from './JobProgressionCard/NoProgression/StartProgressionDialog'
import ReturnFiledDocumentCreationModal from './JobProgressionCard/JobProgressionDocumentCreationModal'
import RequestDraftReturnApprovalModal from './JobProgressionCard/JobProgressionRequestDraftApprovalModal'
import JobProgressionExtensionDocumentCreationModal from './JobProgressionCard/JobProgressionExtensionDocumentCreationModal'
import EstimatedIrsPaymentDialog from './JobProgressionCard/EstimatedIrsPaymentDialog'
import FinalReturnAmountsDialog from './FinalReturnAmountsDialog'
import AddManualInvoiceDialog from './AddManualInvoiceDialog'
import UpdateDeadlineDialog from './UpdateDeadlineDialog'
import SubmitJobDialog from './SubmitJobDialog'
import NotifyProsOfOpenJobDialog from './NotifyProsOfOpenJobDialog'
import ChargeOrRefundDialog from './ChargeOrRefundDialog'
import RateProDialog from '../../../ratings/RateProDialog'
import PtinRequiredView from '../../../../components/PtinRequiredView'
import UpdateJobScopeDialog from './UpdateJobScopeDialog'
import ServiceItemListDialog from './ServiceItemListDialog'
import AddClientsAsTeamMemberDialog from './AddClientsAsTeamMemberDialog'
import RemoveClientsAsNonAdminDialog from './RemoveClientsAsNonAdminDialog'
import OpenItemsDialog from './JobProgressionCard/OpenItems/OpenItemsDialog'
import UpdateProgressionStepDialog from './UpdateProgressionStepDialog'
import BookkeepingCompleteCycleDialog from './BookkeepingCompleteCycleDialog'
import BookkeepingCompleteJobDialog from './BookkeepingCompleteJobDialog'
import BookkeepingSelectCycleDialog from './BookkeepingSelectCycleDialog'
import BookkeepingCorrectCycleDialog from './BookkeepingCorrectCycleDialog'
import BookkeepingCycleDetailsDialog from './BookkeepingCycleDetailsDialog'
import DocumentRequestDialog from './DocumentRequestDialog'
import IncompleteConsultationCompletionDialog from './IncompleteConsultationCompletionDialog'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  vm: JobViewModel
  onJobListSidebarToggleClick?: () => unknown
  onJobListToggleHoverChange: (hovering: boolean) => unknown
  isJobListSidebarVisible: boolean
}

export default observer(function JobCards({
  vm,
  onJobListSidebarToggleClick,
  onJobListToggleHoverChange,
  isJobListSidebarVisible,
}: Props) {
  const sideSheet = useSideSheet()

  function onEnter() {
    if (
      vm.documentsVM.uploadBarVM.hidden ||
      vm.documentsVM.uploadBarVM.resolved
    ) {
      vm.documentsVM.uploadBarVM.setDragging()
    }
  }

  function onLeave() {
    if (!vm.documentsVM.uploadBarVM.uploading) {
      vm.documentsVM.uploadBarVM.setHidden()
    }
  }

  function onDrop(files: FileList) {
    vm.documentsVM.viewDocumentCreation(Array.from(files))
  }

  const [, dropZoneRef] = useDropzone<HTMLDivElement>(onDrop, onEnter, onLeave)

  if (vm.ptinRequired) {
    return <PtinRequiredView />
  }

  return (
    <SideSheetHostView
      elevation={sideSheet.elevation}
      onRequestHide={sideSheet.hide}
      sheetVisible={sideSheet.visible || !sideSheet.shouldBeAbove}
    >
      <div
        className={styles.container}
        ref={vm.job.status !== 'CLOSED' ? dropZoneRef : undefined}
      >
        <HeaderView
          vm={vm}
          onJobListHoverChange={onJobListToggleHoverChange}
          onJobListSidebarToggleClick={onJobListSidebarToggleClick}
          isJobListSidebarVisible={isJobListSidebarVisible}
        />
        <ContentView vm={vm} />
        <DocumentPicker
          searchPlaceholder={`Select documents to add to ${vm.job.name}`}
          vm={vm.documentLinkingPickerVM}
        />
      </div>
      <CreateAmendmentDialog vm={vm.createAmendmentVM} />
      <ApproveDraftDialog vm={vm.approveDraftInReviewDialogVM} />
      <RejectDraftDialog vm={vm.rejectDraftInReviewDialogVM} />
      <StartProgressionDialog
        vm={vm.jobProgressionVM.startProgressionDialogVM}
      />
      <OpenItemsDialog vm={vm.jobProgressionVM.openItemsDialogVM} />
      <RenameJobDialog vm={vm.renameVM} />
      <AddJobNoteDialog vm={vm.jobNotesVM.addNoteVM} />
      <CorrectionsDialog vm={vm} />
      <TransferJobDialog vm={vm.transferJobVM} />
      <RemoveClientTeamDialog vm={vm.removeClientTeamVM} />
      <ForfeitDialog vm={vm} />
      <MessageBankDialog vm={vm.messageBankDialogVM}></MessageBankDialog>
      <CloseJobAsAdminDialog vm={vm.closeJobAsAdminDialogVM} jobVM={vm} />
      <AddCollaboratorsAsAdminDialog vm={vm.addCollaboratorsAsAdminVM} />
      <AddClientsAsTeamMemberDialog vm={vm.addClientsAsTeamMemberVM} />
      <RemoveCollaboratorsAsAdminDialog vm={vm.removeCollaboratorsAsAdminVM} />
      <RemoveClientsAsNonAdminDialog vm={vm.removeClientsAsNonAdminVM} />
      <ReopenJobAsAdminDialog vm={vm.reopenJobAsAdminVM} />
      <RateProDialog vm={vm.ratingsVM.rateProDialogVM} />
      <UpdateJobScopeDialog vm={vm.updateJobScopeVM} />
      <RequestScopeChangesDialog vm={vm.requestScopeChangesVM} />
      <ViewJobScopeChangeRequestDialog vm={vm.viewScopeChangeRequestVM} />
      <CopyJobDialog vm={vm.copyVM} />
      <SplitPayoutDialog vm={vm.splitPayoutDialogVM} />
      <ApplyCouponAsAdminDialog vm={vm.applyCouponAsAdminVM} />
      <RemoveCouponAsAdminDialog vm={vm.removeCouponAsAdminVM} />
      {vm.documentsVM.documentCreationVM && (
        <>
          <DocumentCreationModal vm={vm.documentsVM.documentCreationVM} />
        </>
      )}
      {vm.jobProgressionVM.jobProgressionDocumentCreationVM &&
        vm.jobProgressionVM.isDocumentCreationToggledViewReturnFiled && (
          <>
            <ReturnFiledDocumentCreationModal vm={vm.jobProgressionVM} />
          </>
        )}
      {vm.jobProgressionVM.jobProgressionDocumentCreationVM &&
        vm.jobProgressionVM.isDocumentCreationToggledViewExtension && (
          <>
            <JobProgressionExtensionDocumentCreationModal
              vm={vm.jobProgressionVM}
            />
          </>
        )}
      {vm.jobProgressionVM.requestDraftReturnApprovalVM.documentViewModel && (
        <>
          <RequestDraftReturnApprovalModal
            vm={vm.jobProgressionVM.requestDraftReturnApprovalVM}
          />
        </>
      )}
      <EstimatedIrsPaymentDialog
        vm={vm.jobProgressionVM.estimatedIrsPaymentDialogVM}
      />
      <FinalReturnAmountsDialog
        vm={vm.jobProgressionVM.finalReturnAmountsDialogVM}
      />
      <DeleteJobDialog vm={vm.deleteJobDialogVM} />
      <SubmitJobDialog vm={vm.submitJobDialogVM} />
      <AddManualInvoiceDialog vm={vm.addManualInvoiceDialogVM} />
      <UpdateDeadlineDialog vm={vm.updateDeadlineDialogVM} />
      <NotifyProsOfOpenJobDialog vm={vm.notifyProsOfOpenJobDialogVM} />
      <ChargeOrRefundDialog vm={vm.chargeOrRefundDialogVM} />
      <ServiceItemListDialog vm={vm.serviceItemListVM} />
      <UpdateProgressionStepDialog vm={vm.updateProgressionStepDialogVM} />
      <BookkeepingCompleteCycleDialog
        vm={vm.bookkeepingProgressionVM.bookkeepingCompleteCycleVM}
      />
      <BookkeepingCompleteJobDialog
        vm={vm.bookkeepingProgressionVM.bookkeepingCompleteJobVM}
        jobVM={vm}
      />
      <BookkeepingSelectCycleDialog vm={vm.bookkeepingSelectCycleDialogVM} />
      <BookkeepingCorrectCycleDialog vm={vm.bookkeepingCorrectCycleDialogVM} />
      <BookkeepingCycleDetailsDialog
        vm={vm.bookkeepingProgressionVM.bookkeepingCycleDetailsVM}
      />
      <DocumentRequestDialog vm={vm.documentRequestDialogVM} />
      <IncompleteConsultationCompletionDialog
        vm={vm.incompleteConsultationCompletionDialogVM}
      />
    </SideSheetHostView>
  )
})
